/* eslint-disable import/no-unresolved */
import { Component, ComponentInterface, JSX, Prop, State, h } from '@stencil/core';

import { ButtonType } from '@aglenergy/design-system/dist/types/components/core/button/button.types';
import { StripColorType } from '@aglenergy/design-system-components/dist/types/components/card/static-card/card';
import { ProductCatalogueContentFeature, ProductCatalogueProduct } from 'app/shared/models/index';
import { store } from 'app/shared/store/store';
import { changePlanService } from 'app/mobile/services/change-plan/change-plan.service';
import { productService } from 'app/shared/services/productService/product.service';
import { renderContentBelowCards } from 'app/shared/utilities/helper-functions';

@Component({
  tag: 'mobile-change-plan-product-card',
  styleUrl: 'mobile-change-plan-product-card.scss'
})
export class MobileChangePlanProductCard implements ComponentInterface {
  @Prop() public products: ProductCatalogueProduct[];
  @Prop() public isPersonalisedProductsLoading: boolean;
  @Prop() public focusCardIndex: number;

  @State() private hasValidEnergyContract = false;

  private cardIdPrefix = 'card';

  public async componentWillLoad(): Promise<void> {
    this.hasValidEnergyContract = await changePlanService.hasEnergyContract();
  }

  public componentDidRender(): void {
    this.scrollToTop();
  }

  private getFeatureIcon(featureIconName?: string): JSX.Element {
    if (!featureIconName) {
      return null;
    }
    if (featureIconName.includes('.')) {
      return <dls-dynamic-icon iconPath={featureIconName} altText="" />;
    }
    const iconNameInitial = featureIconName.charAt(0);
    return iconNameInitial >= 'a' && iconNameInitial <= 'l' ? (
      <dls-decorative-icon-set1 id="svg" size="sm" type={featureIconName as any} />
    ) : (
      <dls-decorative-icon-set2 id="svg" size="sm" type={featureIconName as any} />
    );
  }

  public onChangeButtonClick(product: ProductCatalogueProduct): void {
    !this.isPersonalisedProductsLoading && productService.onSelectedMobileProductForChangePlan(this.products, product.sku, 'productswap');
  }

  public onSeePlansButtonClick(): void {
    changePlanService.clearSelectedProduct();
  }

  private scrollToTop(): void {
    const comparisonContainer = document.getElementsByClassName('current-plan-info');
    if (comparisonContainer.length > 0) comparisonContainer[0].scrollIntoView();
  }

  public render(): JSX.Element {
    let anyStripText = this.products?.some((product) => product.content?.stripText);
    const currentPlanSku = changePlanService.getCurrentPlanSku();
    const selectedProductSku = changePlanService.getSelectedProductSku();
    let selectedProductPlan: ProductCatalogueProduct | null = null;
    if (selectedProductSku && currentPlanSku !== selectedProductSku) {
      selectedProductPlan = this.products?.find((prod) => prod.sku === selectedProductSku);
    }

    document.documentElement.style.setProperty('--scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`);
    return (
      <div>
        {this.products?.length > 1 && <h2 class="select-plan-header">Select a new plan</h2>}
        {selectedProductPlan && (
          <div class="selected-plan-title-container">
            <dls-h2 class="selected-plan-title">{`You're changing to ${selectedProductPlan.name}`} Plan</dls-h2>
            <dls-p class="selected-plan-subtitle">
              <strong>Did you see a different price before?</strong> Some offers are for new customers only.
            </dls-p>
          </div>
        )}
        <div class="change-plan-container" id="comparison-container">
          {this.products && (
            <div>
              <carousel-buttons numberOfProducts={this.products.length} focusedCardIndex={this.focusCardIndex} showPagination={true}>
                <div class="card-container" id="card-container">
                  {this.products.map((product, index) => {
                    const isCurrentPlan = currentPlanSku === product.sku;
                    anyStripText = anyStripText || isCurrentPlan;
                    const buttonLabel = isCurrentPlan ? 'Remove plan' : 'Change to this plan';
                    const buttonType = isCurrentPlan ? 'secondary' : 'primary';
                    const hasSelectedPlan = selectedProductSku !== null && currentPlanSku !== selectedProductSku;
                    return this.mobileCardRender(isCurrentPlan, anyStripText, hasSelectedPlan, product, index, buttonType, buttonLabel);
                  })}
                </div>
              </carousel-buttons>
              {selectedProductSku && selectedProductSku !== currentPlanSku && this.ctaButtonsRender()}
            </div>
          )}
        </div>
        <div class="links">
          {this.products &&
            this.products.length > 0 &&
            this.products
              .find((product) => product.content != null)
              ?.content?.links?.filter((link) => link.title.toLowerCase() !== 'sim promotion terms')
              .map((link) => (
                <div key={link.url}>
                  <dls-link href={link.url} open-in-new-window>
                    {link.title}
                  </dls-link>
                </div>
              ))}
        </div>
        <div class="below-cards-message">
          <message-wrapper
            message={`<p>BYO phone. All data for use in Australia. Excess data usage charges apply.<sup>2</sup></p>
              <p>You can change your plan once a month. If you change before the end of your billing period, you’ll need to pay a pro-rata amount for your new plan for the rest of the billing period plus a month in advance. You’ll also receive a pro-rata refund for fees paid in advance for your current plan, not including any data boost payments.</p>
              <p>When you change plan, the data allowance of your new plan will apply and any unused data allowance and Data Boosts will expire.</p>
              `}
          />
          {renderContentBelowCards(this.products)}
        </div>
      </div>
    );
  }

  private mobileCardRender(
    isCurrentPlan: boolean,
    anyStripText: boolean,
    hasSelectedPlan: boolean,
    product: ProductCatalogueProduct,
    index: number,
    buttonType: ButtonType,
    buttonLabel: string
  ): any {
    const displayToUser = product.qualified !== false;
    let stripText = product.content?.stripText;
    let stripColor: StripColorType = anyStripText ? 'secondary-04' : 'none';
    if (isCurrentPlan) {
      stripText = 'Current plan';
      stripColor = 'none';
    }

    const bundlePriceDesc = 'when you have AGL energy.';

    return (
      <div class="card__wrapper">
        <div class={`card scroll-snap ${!displayToUser ? 'unavailable' : ''}`} id={`${this.cardIdPrefix}${index}`}>
          {/*
            // https://aglenergy.atlassian.net/browse/TEC-26252: changes to remove the current plan
            // from IPP page as per design due to incorrect pricing. Commented following code
            // until future notice.
           isSelectedPlan && (
            <div class="card__plan-selected__strip-icon">
              <agl-ds-icon icon-path="/apps/comparison/assets/icon/agl_tile_tick.svg" size="xs" />
            </div>
          )*/}
          <dls-card
            padding="md"
            elevationType="level1"
            radius="round02"
            stripColor={stripText ? stripColor : 'none'}
            stripText={stripText}
            topMargin={hasSelectedPlan ? 'space03' : 'space06'}
          >
            <div>
              <dls-h3 styledAs="title4">
                <div class="plan-name">{product.name}</div>
              </dls-h3>
            </div>
            <div class="unavailable-overlay-container">
              <div class={!displayToUser && 'unavailable-overlay'} hidden={displayToUser}>
                <info-tag heading={product.content?.unavailableHeading} content={product.content?.unavailableContent} />
              </div>
              <div>
                <telco-new-price-tag
                  price={product.fibreUpgrade ? product.content?.bundleOfferPrice : product.attributes.agl_marketing_price}
                  paymentFreq={product.content ? product.content?.priceVariable : 'a month'}
                  priceDesc={bundlePriceDesc}
                  bundleTitle={product.content ? product.content?.bundleOfferStrip : 'AGL Bundle Offer'}
                />
                {!this.hasValidEnergyContract && (
                  <telco-new-price-tag
                    price={product.content?.withoutOfferPrice || product.price}
                    paymentFreq={product.content ? product.content?.priceVariable : 'a month'}
                    priceDesc={product.content ? product.content?.withoutOfferText : ''}
                  />
                )}
              </div>
              {product.content && product.content.features ? (
                <div class="feature-list">
                  {product.content?.features.map((feature: ProductCatalogueContentFeature) => {
                    return (
                      <telco-feature-text desc={feature.featureDescription}>{this.getFeatureIcon(feature.featureIcon)}</telco-feature-text>
                    );
                  })}
                </div>
              ) : (
                <div class="feature-list-empty">Sorry we can't show this plans features right now</div>
              )}
              {!isCurrentPlan && !hasSelectedPlan ? (
                <div class="cta--button">
                  <dls-button
                    variant={buttonType}
                    loading={this.isPersonalisedProductsLoading || store.get('skuBeingRemoved') === product.sku}
                    data-testid="cta-main"
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => displayToUser && this.onChangeButtonClick(product)}
                  >
                    {buttonLabel}
                  </dls-button>
                </div>
              ) : (
                <div class={!hasSelectedPlan ? 'cta--empty-button-container' : ''} />
              )}
            </div>
          </dls-card>
        </div>
      </div>
    );
  }

  private ctaButtonsRender() {
    const product = this.products.find((p) => p.sku === changePlanService.getSelectedProductSku());
    return (
      <div class="cta--container">
        <dls-button
          variant={'primary'}
          loading={this.isPersonalisedProductsLoading}
          data-testid="cta-main"
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => this.onChangeButtonClick(product)}
        >
          {'Change to this plan'}
        </dls-button>
        <dls-button
          variant={'tertiary'}
          loading={this.isPersonalisedProductsLoading}
          data-testid="cta-secondary"
          // eslint-disable-next-line react/jsx-no-bind
          onClick={this.onSeePlansButtonClick}
        >
          {'See other SIM plans'}
        </dls-button>
      </div>
    );
  }
}
